import {
  Button,
  Card,
  Space,
  Tag,
  Select,
  message,
  Form,
  Input,
  Row,
  Col,
  DatePicker,
} from "antd";
import React, { useEffect, useState } from "react";
import { EyeOutlined, MessageOutlined } from "@ant-design/icons";
import { API_METHODS, BLANK_VALUE } from "../../../constants";
import useApi from "../../../hooks/useApi";
import TableComponent from "../../common/TableComponent";
import RespondToInquiry from "./RespondToInquiry";
import ViewInquiry from "./ViewInquiry"; // Import the new component

const { Option } = Select;
const { RangePicker } = DatePicker;

function ContactInquiryTable() {
  const [messageApi, contextHolder] = message.useMessage();

  // State management - Separated view and respond states
  const [respondVisible, setRespondVisible] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Separate state for viewing inquiry
  const [viewVisible, setViewVisible] = useState(false);
  const [viewInquiryData, setViewInquiryData] = useState(null);

  // API calls
  const [responseData, getRequest] = useApi(
    "/admin/contact",
    null,
    API_METHODS.GET
  );
  const [updateStatusResponse, updateStatusRequest] = useApi();
  const [inquiryDetails, getInquiryDetails] = useApi();

  const [form] = Form.useForm();

  // Table columns
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 140,
      ellipsis: true,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      width: 180,
      ellipsis: true,
      render: (text) => (
        <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 160 }}>
          {text}
        </div>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_on",
      key: "created_on",
      width: 100,
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => {
        const getStatusColor = (status) => {
          switch (status) {
            case "new":
              return "orange";
            case "in_progress":
              return "blue";
            case "resolved":
              return "green";
            default:
              return "default";
          }
        };
        return (
          <Tag color={getStatusColor(status)}>{status?.toUpperCase()}</Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      width: 320,
      render: (text, record) => (
        <Space>
          <Button
            size="small"
            onClick={() => viewInquiry(record)}
            icon={<EyeOutlined />}
            style={{ backgroundColor: "#f97316", borderColor: "#f97316", color: "#fff" }}
          >
            View
          </Button>
          <Button
            size="small"
            onClick={() => respondToInquiry(record)}
            icon={<MessageOutlined />}
            style={{ backgroundColor: "#1677ff", borderColor: "#1677ff", color: "#fff" }}
          >
            Respond
          </Button>
          <Select
            size="small"
            value={record.status}
            onChange={(value) => updateStatus(record.id, value)}
            style={{ width: 120 }}
          >
            <Option value="new">New</Option>
            <Option value="in_progress">In Progress</Option>
            <Option value="resolved">Resolved</Option>
          </Select>
        </Space>
      ),
    },
  ];

  // Load data on component mount
  useEffect(() => {
    getRequest();
  }, []);

  // Process API responses
  useEffect(() => {
    const data = responseData.data !== BLANK_VALUE ? responseData.data : [];
    const pagination =
      responseData.pagination !== BLANK_VALUE ? responseData.pagination : {};
    setTableData(data);
    setTotalRecords(pagination ? pagination.count : 0);
  }, [responseData]);

  // Handle status update success
  useEffect(() => {
    if (updateStatusResponse.data && !updateStatusResponse.isLoading) {
      getRequest();
      messageApi.open({
        type: "success",
        content: "Status updated successfully",
      });
    }
  }, [updateStatusResponse]);

  // Handle inquiry details API response for viewing
  useEffect(() => {
    if (inquiryDetails.data && !inquiryDetails.isLoading) {
      setViewInquiryData(inquiryDetails.data);
      setViewVisible(true);
    }
  }, [inquiryDetails]);

  // Action handlers
  const respondToInquiry = (record) => {
    setSelectedInquiry(record);
    setRespondVisible(true);
  };

  const viewInquiry = (record) => {
    // Clear previous data and fetch new details
    setViewInquiryData(null);
    getInquiryDetails(null, API_METHODS.GET, `/admin/contact/${record.id}`);
  };

  const updateStatus = (inquiryId, newStatus) => {
    updateStatusRequest(
      { status: newStatus },
      API_METHODS.PUT,
      `/admin/contact/update-status/${inquiryId}`
    );
  };

  const handleRespondModal = (isUpdated) => {
    setRespondVisible(false);
    setSelectedInquiry(null);
    if (isUpdated === true) {
      getRequest();
      setCurrentPage(1);
    }
  };

  const handleViewModal = () => {
    setViewVisible(false);
    setViewInquiryData(null);
  };

  // Filter functionality
  const filterResults = () => {
    const values = form.getFieldsValue();
    setCurrentPage(1);
    let url = `/admin/contact?pageNumber=1`;

    if (values.status) {
      url += `&status=${values.status}`;
    }
    if (values.search) {
      url += `&search=${values.search}`;
    }
    if (values.dateRange && values.dateRange.length === 2) {
      url += `&startDate=${values.dateRange[0].format("YYYY-MM-DD")}`;
      url += `&endDate=${values.dateRange[1].format("YYYY-MM-DD")}`;
    }

    getRequest(null, API_METHODS.GET, url);
  };

  const resetFilter = () => {
    form.resetFields();
    setCurrentPage(1);
    getRequest();
  };

  // Pagination
  const onPaginationChange = (page) => {
    const values = form.getFieldsValue();
    let url = `/admin/contact?pageNumber=${page.current}`;

    if (values.status) {
      url += `&status=${values.status}`;
    }
    if (values.search) {
      url += `&search=${values.search}`;
    }
    if (values.dateRange && values.dateRange.length === 2) {
      url += `&startDate=${values.dateRange[0].format("YYYY-MM-DD")}`;
      url += `&endDate=${values.dateRange[1].format("YYYY-MM-DD")}`;
    }

    getRequest(null, API_METHODS.GET, url);
    setCurrentPage(page.current);
  };

  return (
    <Card title="🙏 Contact Inquiries Management">
      {contextHolder}

      {/* Filter Section */}
      <Form form={form} style={{ marginBottom: "24px" }}>
        <Row gutter={16}>
          <Col xs={24} sm={8} md={6}>
            <Form.Item name="search" label="Search">
              <Input placeholder="Name, Email, or Subject" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Form.Item name="status" label="Status">
              <Select placeholder="Select Status" allowClear>
                <Option value="new">New</Option>
                <Option value="in_progress">In Progress</Option>
                <Option value="resolved">Resolved</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8} md={8}>
            <Form.Item name="dateRange" label="Date Range">
              <RangePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={4}>
            <Form.Item label=" ">
              <Space>
                <Button type="primary" onClick={filterResults}>
                  Search
                </Button>
                <Button onClick={resetFilter}>Reset</Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {/* Table */}
      <TableComponent
        columns={columns}
        tableData={tableData}
        loading={responseData.isLoading}
        onPaginationChange={onPaginationChange}
        total={totalRecords}
        current={currentPage}
        scrollX={1090}
      />

      {/* Respond Modal */}
      {respondVisible && (
        <RespondToInquiry
          visible={respondVisible}
          close={handleRespondModal}
          selectedInquiry={selectedInquiry}
        />
      )}

      {/* View Inquiry Modal */}
      {viewVisible && (
        <ViewInquiry
          visible={viewVisible}
          close={handleViewModal}
          inquiryData={viewInquiryData}
        />
      )}
    </Card>
  );
}

export default ContactInquiryTable;
